import { notification } from 'antd';
import { Dispatch } from 'react';

import { ApiError, BranchApi } from 'api';
import { OnboardingApi } from 'api/organization/OnboardingApi';
import {
  CreateBranchRegistrationWorkflowDto,
  DeleteBranchRegistrationWorkflowDto,
  ExcludeBranchesFromRegistrationWorkflowDto,
  IncludeBranchesToRegistrationWorkflowDto,
  OnboardingDto,
  UpdateBranchRegistrationWorkflowCredentialsDto,
} from 'api/organization/onboardingTypes';
import { UserProfile } from 'api/user-profile/userProfileTypes';
import { RbacRoleKeys } from 'constants/rbac';

import container from 'ioc-container/container';
import Service from 'ioc-container/service-keys';
import { PosthogServiceInterface } from 'services/PosthogService';
import { OnboardingContextState, setBranches, setOnboarding, setStep, Step } from './onboardingReducer';
import { flowSelector } from './onboardingSelectors';
import { currentUserSelector } from '../../authentication/context/authSelectors';
import { useAuthState } from '../../authentication/context/authContext';

const handleError = async (
  message: string,
  error: unknown,
  config?: {
    notification?: string;
  },
) => {
  const apiError = await (async () => {
    if (error instanceof ApiError) {
      const responseJson = await error.response.json();
      return { ...error, responseJson };
    }
    return null;
  })();
  console.error(message, apiError ?? error);
  if (config?.notification) {
    notification.error({
      message: config.notification,
      description: (() => {
        const responseJson = apiError?.responseJson;
        if (typeof responseJson === 'string') {
          return responseJson;
        }
        const responseJsonMessage = responseJson?.message;
        if (typeof responseJsonMessage === 'string') {
          return responseJsonMessage;
        }
        if (Array.isArray(responseJsonMessage) && typeof responseJsonMessage[0]?.constraints === 'object') {
          const constraints = responseJsonMessage[0]?.constraints;
          return Object.keys(constraints).map((key) => constraints[key])[0];
        }
        const responseJsonError = responseJson?.error;
        if (typeof responseJsonError === 'string') {
          return responseJsonError;
        }
        const responseJsonErrorErrors = responseJson?.error?.errors;
        if (typeof responseJsonErrorErrors?.[0] === 'string') {
          return responseJsonErrorErrors[0];
        }
        return null;
      })(),
    });
  }
};

export const fetchOnboarding = () => async (dispatch: Dispatch<any>, getState: () => OnboardingContextState) => {
  const flow = flowSelector(getState());
  const onboarding = flow.id ? await OnboardingApi.getBySharedFlow(flow.id, flow.secret) : await OnboardingApi.get();
  dispatch(setOnboarding(onboarding));
  return onboarding;
};

export const initializeOnboarding = (user: UserProfile | null) => async (dispatch: Dispatch<any>, getState: () => OnboardingContextState) => {
  const letUserToDashboard = async () => {
    const branches = await BranchApi.getUserBranches();
    dispatch(setBranches(branches));
    dispatch(setStep(null));
  };
  if (user && user.rbac_role_id !== RbacRoleKeys.OrganizationAdmin) {
    dispatch(setOnboarding(null));
    return letUserToDashboard();
  }
  const onboarding = (await dispatch(fetchOnboarding())) as unknown as OnboardingDto;
  dispatch(setStep(undefined));
  if (user && (!onboarding || onboarding.isAdminFlowCompleted)) {
    return letUserToDashboard();
  }
  if (onboarding) {
    dispatch(setStep(Step.Setup));
  }
};

export const shareFlow = (
  collaboratorEmail?: string,
  config?: {
    errorNotification?: string;
    successNotification?: string;
  },
) => async (dispatch: Dispatch<any>) => {
  try {
    const onboarding = await OnboardingApi.shareFlow(collaboratorEmail);
    dispatch(setOnboarding(onboarding));
    if (config?.successNotification) {
      notification.success({ message: config?.successNotification });
    }
  } catch (error) {
    handleError('Error sharing flow:', error, {
      notification: config?.errorNotification,
    });
  }
};

export const waitForBranchesSearchCompletion = (
  branchRegistrationWorkflowId: string,
  config?: {
    errorNotification?: string;
    successNotification?: string;
  },
) => async (dispatch: Dispatch<any>, getState: () => OnboardingContextState) => {
  const flow = flowSelector(getState());
  try {
    const onboarding = flow.id
      ? await OnboardingApi.getBySharedFlow(flow.id, flow.secret)
      : await OnboardingApi.get();
    const branchRegistrationWorkflow = onboarding?.branchRegistrationWorkflows.find(
      (v) => v.id === branchRegistrationWorkflowId,
    );
    if (
      branchRegistrationWorkflow != null
        && branchRegistrationWorkflow.areCredentialsValid == true
        && branchRegistrationWorkflow.foundProviders == null
        && branchRegistrationWorkflow.error == null
    ) {
      setTimeout(() => dispatch(waitForBranchesSearchCompletion(branchRegistrationWorkflow.id)), 1000);
    } else {
      dispatch(setOnboarding(onboarding));
    }
    if (config?.successNotification) {
      notification.success({ message: config?.successNotification });
    }
  } catch (error) {
    handleError('Error waiting for branches search completion:', error, {
      notification: config?.errorNotification,
    });
  }
};

export const createBranchRegistrationWorkflow = (
  input: Omit<CreateBranchRegistrationWorkflowDto, 'secret'>,
  config?: {
    errorNotification?: string;
    errorWaitingForBranchesSearchCompletionNotification?: string;
    successNotification?: string;
  },
) => async (dispatch: Dispatch<any>, getState: () => OnboardingContextState) => {
  const flow = flowSelector(getState());
  const dto = { secret: flow.secret, ...input };
  try {
    const onboarding = flow.id
      ? await OnboardingApi.createBranchRegistrationWorkflowBySharedFlow(flow.id, dto)
      : await OnboardingApi.createBranchRegistrationWorkflow(dto);
    dispatch(setOnboarding(onboarding));
    const lastCreatedBranchRegistrationWorkflow = [...onboarding.branchRegistrationWorkflows].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )[0];
    dispatch(
      waitForBranchesSearchCompletion(lastCreatedBranchRegistrationWorkflow.id, {
        errorNotification: config?.errorWaitingForBranchesSearchCompletionNotification,
      }),
    );
    if (config?.successNotification) {
      notification.success({ message: config?.successNotification });
    }
  } catch (error) {
    handleError('Error creating branch registration workflow:', error, {
      notification: config?.errorNotification,
    });
  }
};

export const updateBranchRegistrationWorkflowCredentials = (
  input: Omit<UpdateBranchRegistrationWorkflowCredentialsDto, 'secret'>,
  config?: {
    errorNotification?: string;
    errorWaitingForBranchesSearchCompletionNotification?: string;
    successNotification?: string;
  },
) => async (dispatch: Dispatch<any>, getState: () => OnboardingContextState) => {
  const flow = flowSelector(getState());
  const dto = { secret: flow.secret, ...input };
  try {
    const onboarding = flow.id
      ? await OnboardingApi.updateBranchRegistrationWorkflowCredentialsBySharedFlow(flow.id, dto)
      : await OnboardingApi.updateBranchRegistrationWorkflowCredentials(dto);
    dispatch(setOnboarding(onboarding));
    dispatch(
      waitForBranchesSearchCompletion(dto.id, {
        errorNotification: config?.errorWaitingForBranchesSearchCompletionNotification,
      }),
    );
    if (config?.successNotification) {
      notification.success({ message: config?.successNotification });
    }
  } catch (error) {
    handleError('Error updating branch registration workflow credentials:', error, {
      notification: config?.errorNotification,
    });
  }
};

export const excludeBranchesFromRegistrationWorkflow = (
  input: Omit<ExcludeBranchesFromRegistrationWorkflowDto, 'secret'>,
  config?: {
    errorNotification?: string;
    successNotification?: string;
  },
) => async (dispatch: Dispatch<any>, getState: () => OnboardingContextState) => {
  const flow = flowSelector(getState());
  const dto = { secret: flow.secret, ...input };
  try {
    const onboarding = flow.id
      ? await OnboardingApi.excludeBranchesFromRegistrationWorkflowBySharedFlow(flow.id, dto)
      : await OnboardingApi.excludeBranchesFromRegistrationWorkflow(dto);
    dispatch(setOnboarding(onboarding));
    if (config?.successNotification) {
      notification.success({ message: config?.successNotification });
    }
  } catch (error) {
    handleError('Error excluding branches from registration workflow:', error, {
      notification: config?.errorNotification,
    });
  }
};

export const includeBranchesToRegistrationWorkflow = (
  input: Omit<IncludeBranchesToRegistrationWorkflowDto, 'secret'>,
  config?: {
    errorNotification?: string;
    successNotification?: string;
  },
) => async (dispatch: Dispatch<any>, getState: () => OnboardingContextState) => {
  const flow = flowSelector(getState());
  const dto = { secret: flow.secret, ...input };
  try {
    const onboarding = flow.id
      ? await OnboardingApi.includeBranchesToRegistrationWorkflowBySharedFlow(flow.id, dto)
      : await OnboardingApi.includeBranchesToRegistrationWorkflow(dto);
    dispatch(setOnboarding(onboarding));
    if (config?.successNotification) {
      notification.success({ message: config?.successNotification });
    }
  } catch (error) {
    handleError('Error including branches to registration workflow:', error, {
      notification: config?.errorNotification,
    });
  }
};

export const deleteBranchRegistrationWorkflow = (
  input: Omit<DeleteBranchRegistrationWorkflowDto, 'secret'>,
  config?: {
    errorNotification?: string;
    successNotification?: string;
  },
) => async (dispatch: Dispatch<any>, getState: () => OnboardingContextState) => {
  const flow = flowSelector(getState());
  const dto = { secret: flow.secret, ...input };
  try {
    const onboarding = flow.id
      ? await OnboardingApi.deleteBranchRegistrationWorkflowBySharedFlow(flow.id, dto)
      : await OnboardingApi.deleteBranchRegistrationWorkflow(dto);
    dispatch(setOnboarding(onboarding));
    if (config?.successNotification) {
      notification.success({ message: config?.successNotification });
    }
  } catch (error) {
    handleError('Error deleting branch registration workflow:', error, {
      notification: config?.errorNotification,
    });
  }
};

export const completeFlow = (config?: { errorNotification?: string; successNotification?: string }) => async (dispatch: Dispatch<any>, getState: () => OnboardingContextState) => {
  const flow = flowSelector(getState());
  try {
    const onboarding = flow.id
      ? await OnboardingApi.completeOwnFlow(flow.id, { secret: flow.secret })
      : await OnboardingApi.completeAdminFlow();
    dispatch(setOnboarding(onboarding));
    dispatch(setStep(null));
    if (config?.successNotification) {
      notification.success({ message: config?.successNotification });
    }
    const state = useAuthState();
    const user = currentUserSelector(state);
    const posthogService = container.resolve<PosthogServiceInterface>(Service.posthog);
    if (user) {
      posthogService.captureOnboardingCompletedEvent(user);
      posthogService.identify(user);
    }
  } catch (error: unknown) {
    handleError('Error completing flow:', error, {
      notification: config?.errorNotification,
    });
  }
};
